import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Send, MessageSquare } from "lucide-react";
import { AdminReferral, ReferralStatus, type RecruiterNote } from "@/lib/schema";
import { AdminReferralWithUI } from "@/api/referrals";
import { formatDateForSouthAfrica } from "@/lib/textUtils";

interface NotesActionsTabProps {
  referral: AdminReferralWithUI;
  newNote: string;
  onNewNoteChange: (note: string) => void;
  onAddNote: () => void;
  onStatusChange: (status: ReferralStatus) => void;
}

export function NotesActionsTab({ 
  referral, 
  newNote, 
  onNewNoteChange, 
  onAddNote, 
  onStatusChange, 
}: NotesActionsTabProps) {
  const notes: RecruiterNote[] = referral.recruiterNote || [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="space-grotesk text-lg">Status Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[ReferralStatus.SHORTLISTED, ReferralStatus.INTERVIEWED, ReferralStatus.UNDER_REVIEW,  ReferralStatus.HIRED, ReferralStatus.REJECTED]
              .filter(status => status !== referral.status)
              .map((status) => (
              <Button
                key={status}
                variant={status === ReferralStatus.HIRED ? "primary" : status === ReferralStatus.REJECTED ? "destructive" : "secondary"}
                className="w-full justify-start space-grotesk hover:bg-gray-50"
                onClick={() => onStatusChange(status)}
                data-testid={`button-status-${status}`}
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Move to {status.replace('_', ' ').toUpperCase()}
              </Button>
            ))}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="space-grotesk text-lg">Add Note</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={newNote}
              onChange={(e) => onNewNoteChange(e.target.value)}
              placeholder="Add your notes about this candidate..."
              className="space-grotesk"
              rows={4}
              data-testid="input-new-note"
            />
            <Button
              onClick={onAddNote}
              variant="primary"
              className="w-full bg-orange-500 hover:bg-orange-600 text-white space-grotesk"
              data-testid="button-add-note"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Add Note
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="space-grotesk text-lg">Notes History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {notes.length === 0 ? (
              <p className="text-sm text-gray-500 space-grotesk text-center py-4">No notes added yet</p>
            ) : (
              notes
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) // Sort by date, newest first
                .map((noteObj, index) => (
                  <div key={index} className="border-l-4 border-l-orange-500 pl-4 py-2">
                    <p className="text-sm text-gray-700 space-grotesk">{noteObj.note}</p>
                    <p className="text-xs text-gray-500 space-grotesk mt-1">
                      Added on {formatDateForSouthAfrica(noteObj.date)}
                    </p>
                  </div>
                ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
